// src/pages/BlogPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { parseTags, asString } from '../lib/tagHelpers';

const PAGE_SIZE = 9;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(p) || p < 1 ? 1 : p;
  }, [searchParams]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / PAGE_SIZE)), [count]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const from = (page - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const nowIso = new Date().toISOString();

        const { data, error, count: total } = await supabase
          .from('blog_posts')
          .select(
            'id,title,slug,excerpt,cover_image,author,created_at,publish_at,tags,published',
            { count: 'exact' }
          )
          .eq('published', true)
          .lte('publish_at', nowIso) // only show posts whose publish_at has passed
          .order('publish_at', { ascending: false })
          .range(from, to);

        if (error) {
          console.error('Error fetching blog posts:', error);
          setPosts([]);
          setCount(0);
        } else {
          setCount(total || 0);
          const processed = (data || []).map((post) => ({
            ...post,
            tags: parseTags(post.tags),
          }));
          setPosts(processed);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setPosts([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [page]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setSearchParams(next === 1 ? {} : { page: String(next) });
  };

  return (
    <div
      style={{
        background: 'linear-gradient(to bottom, #0a0a0a 0%, #1a1a1a 100%)',
        color: 'white',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <header
        style={{
          borderBottom: '1px solid #2a2a2a',
          padding: '1rem 1rem',
          position: 'sticky',
          top: 0,
          backgroundColor: 'rgba(10, 10, 10, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              textDecoration: 'none',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>🔬</span>
            <span
              style={{
                fontSize: 'clamp(1.1rem, 4vw, 1.5rem)',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #e07a5f 0%, #f4a261 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              WhyDidItDie
            </span>
          </Link>
          <nav style={{ display: 'flex', gap: '1.5rem', fontSize: '0.9rem' }}>
            <Link
              to="/"
              style={{ color: '#aaa', textDecoration: 'none', transition: 'color 0.2s' }}
            >
              Home
            </Link>
            <Link
              to="/blog"
              style={{ color: '#e07a5f', textDecoration: 'none', fontWeight: '600' }}
            >
              Blog
            </Link>
            <Link
              to="/subscribe"
              style={{ color: '#aaa', textDecoration: 'none', transition: 'color 0.2s' }}
            >
              📧 Subscribe
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div
        style={{
          padding: 'clamp(2rem, 8vw, 4rem) 1rem clamp(1.5rem, 6vw, 3rem)',
          textAlign: 'center',
          background:
            'radial-gradient(ellipse at top, rgba(224, 122, 95, 0.15), transparent)',
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h1
            style={{
              fontSize: 'clamp(1.75rem, 6vw, 3.5rem)',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, #e07a5f 0%, #f4a261 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: '800',
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
            }}
          >
            AI Marketing Intelligence & Campaign Forensics
          </h1>
          <p
            style={{
              fontSize: 'clamp(1rem, 3vw, 1.25rem)',
              color: '#bbb',
              lineHeight: '1.6',
              maxWidth: '720px',
              margin: '0 auto 1.5rem',
              padding: '0 1rem',
            }}
          >
            Weekly insights on campaign waste detection, AI-powered marketing intelligence,
            global platform strategies, psychological copywriting, and decision
            accountability. The stuff your competitors are reading.
          </p>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1rem',
              backgroundColor: 'rgba(224, 122, 95, 0.1)',
              border: '1px solid rgba(224, 122, 95, 0.3)',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              marginTop: '1rem',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>💡</span>
            <div style={{ textAlign: 'left' }}>
              <p
                style={{
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  color: '#f4a261',
                  fontWeight: '600',
                  margin: '0 0 0.25rem 0',
                }}
              >
                Not subscribed yet?
              </p>
              <Link
                to="/subscribe"
                style={{
                  color: '#e07a5f',
                  textDecoration: 'none',
                  fontSize: 'clamp(0.85rem, 2.3vw, 0.95rem)',
                  fontWeight: '500',
                }}
              >
                Subscribe before we start charging for this →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid #2a2a2a',
                borderTop: '3px solid #e07a5f',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }}
            />
            <p style={{ marginTop: '1rem', color: '#888' }}>
              Loading marketing intelligence...
            </p>
          </div>
        ) : posts.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'clamp(2rem, 6vw, 4rem) 1rem',
              backgroundColor: '#151515',
              borderRadius: '16px',
              border: '1px solid #2a2a2a',
              margin: '1rem',
            }}
          >
            <span
              style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}
            >
              🔬
            </span>
            <p
              style={{
                fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                color: '#bbb',
                marginBottom: '0.5rem',
              }}
            >
              No posts yet
            </p>
            <p
              style={{
                color: '#888',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              }}
            >
              Campaign autopsies and AI intelligence dropping soon.
            </p>
          </div>
        ) : (
          <>
            {/* grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
                gap: 'clamp(1rem, 3vw, 2rem)',
                padding: '0 0.5rem',
              }}
            >
              {posts.map((post) => {
                const tags = post.tags;
                const title = asString(post.title);
                const excerpt = asString(post.excerpt);
                const coverImage = asString(post.cover_image);
                const slug = asString(post.slug);
                const author = asString(post.author || 'Isabella Ibeji');
                const displayDate = post.publish_at || post.created_at;

                return (
                  <article
                    key={post.id}
                    style={{
                      backgroundColor: '#151515',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '1px solid #2a2a2a',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = '#e07a5f';
                      e.currentTarget.style.boxShadow =
                        '0 12px 24px rgba(224, 122, 95, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = '#2a2a2a';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {coverImage && (
                      <div
                        style={{
                          width: '100%',
                          height: 'clamp(180px, 30vw, 220px)',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={coverImage}
                          alt={title}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform = 'scale(1.05)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = 'scale(1)')
                          }
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '60px',
                            background:
                              'linear-gradient(to top, #151515, transparent)',
                          }}
                        />
                      </div>
                    )}

                    <div
                      style={{
                        padding: 'clamp(1rem, 3vw, 1.5rem)',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {tags && tags.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '6px',
                            flexWrap: 'wrap',
                            marginBottom: '1rem',
                          }}
                        >
                          {tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              style={{
                                fontSize: 'clamp(0.7rem, 2vw, 0.75rem)',
                                padding: '4px 10px',
                                backgroundColor: 'rgba(224, 122, 95, 0.1)',
                                color: '#f4a261',
                                borderRadius: '4px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              {String(tag)}
                            </span>
                          ))}
                        </div>
                      )}

                      <h2
                        style={{
                          fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
                          marginBottom: '0.75rem',
                          lineHeight: '1.3',
                          fontWeight: '700',
                        }}
                      >
                        <Link
                          to={`/blog/${slug}`}
                          style={{
                            color: 'white',
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = '#e07a5f')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = 'white')
                          }
                        >
                          {title}
                        </Link>
                      </h2>

                      <p
                        style={{
                          color: '#999',
                          marginBottom: '1rem',
                          fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>
                          📅{' '}
                          {new Date(displayDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span>•</span>
                        <span>✍️ {author}</span>
                      </p>

                      <p
                        style={{
                          color: '#ccc',
                          fontSize: 'clamp(0.9rem, 2.8vw, 1rem)',
                          lineHeight: '1.6',
                          marginBottom: '1.5rem',
                          flex: 1,
                        }}
                      >
                        {excerpt}
                      </p>

                      <div style={{ marginTop: 'auto' }}>
                        <Link
                          to={`/blog/${slug}`}
                          style={{
                            color: '#e07a5f',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'gap 0.2s',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.gap = '0.75rem')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.gap = '0.5rem')
                          }
                        >
                          Read full breakdown <span>→</span>
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* pagination */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '2rem',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                style={{
                  padding: '0.6rem 0.9rem',
                  borderRadius: 8,
                  border: '1px solid #2a2a2a',
                  background: page <= 1 ? '#111' : '#151515',
                  color: page <= 1 ? '#555' : '#eee',
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Prev
              </button>

              {/* page numbers (compact) */}
              {Array.from({ length: totalPages }).slice(0, 7).map((_, idx) => {
                const n = idx + 1;
                const show =
                  totalPages <= 7 ||
                  n === 1 ||
                  n === totalPages ||
                  Math.abs(n - page) <= 1 ||
                  (page <= 3 && n <= 5) ||
                  (page >= totalPages - 2 && n >= totalPages - 4);

                if (!show) return null;

                const isActive = n === page;
                return (
                  <button
                    key={n}
                    onClick={() => goToPage(n)}
                    style={{
                      padding: '0.6rem 0.9rem',
                      borderRadius: 8,
                      border: isActive
                        ? '1px solid #e07a5f'
                        : '1px solid #2a2a2a',
                      background: isActive
                        ? 'rgba(224,122,95,0.1)'
                        : '#151515',
                      color: '#eee',
                      cursor: 'pointer',
                      minWidth: 44,
                    }}
                  >
                    {n}
                  </button>
                );
              })}

              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                style={{
                  padding: '0.6rem 0.9rem',
                  borderRadius: 8,
                  border: '1px solid #2a2a2a',
                  background: page >= totalPages ? '#111' : '#151515',
                  color: page >= totalPages ? '#555' : '#eee',
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer CTA */}
      <div
        style={{
          padding: 'clamp(2rem, 6vw, 3rem) 1rem',
          marginTop: 'clamp(2rem, 6vw, 4rem)',
          textAlign: 'center',
          borderTop: '1px solid #2a2a2a',
          background:
            'radial-gradient(ellipse at bottom, rgba(224, 122, 95, 0.08), transparent)',
        }}
      >
        <p
          style={{
            color: '#f4a261',
            marginBottom: '0.5rem',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            fontWeight: '600',
          }}
        >
          Ready to Stop Wasting Your Marketing Budget?
        </p>
        <p
          style={{
            color: '#888',
            marginBottom: '1.5rem',
            fontSize: 'clamp(0.85rem, 2.3vw, 0.95rem)',
            maxWidth: '600px',
            margin: '0 auto 1.5rem',
          }}
        >
          Get access to 8 AI-powered modules: Campaign Detective, Waste Hunter, Deep
          Analysis, Aura AI, Platform Intelligence, Market Intelligence, Content Engine,
          and Decision Tracking.
        </p>
        <Link
          to="/"
          style={{
            display: 'inline-block',
            padding:
              'clamp(0.65rem, 2vw, 0.75rem) clamp(1.5rem, 4vw, 2rem)',
            backgroundColor: '#e07a5f',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f4a261';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#e07a5f';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          Start Free 2-Days Trial →
        </Link>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BlogPage;